import { useEffect, useState } from 'react'
import { X, Store, Save, Loader2, AlertCircle, Check, Landmark, UserRound, Building2, User } from 'lucide-react'
import { apiGet, patchJson } from '../../lib/api.js'
import { xanoMessage } from '../../lib/expense.js'
import Combobox from '../Combobox.jsx'
import { THAI_BANKS } from '../../data/banks.js'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 transition'
const LABEL = 'block text-[12px] font-medium text-slate-500 mb-1'

function Field({ label, children }) {
  return <label className="block"><span className={LABEL}>{label}</span>{children}</label>
}

function Group({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <span className="w-7 h-7 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center"><Icon size={15} strokeWidth={2} /></span>
        <h3 className="text-[13.5px] font-semibold text-slate-800">{title}</h3>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  )
}

// Editable fields on the seller master. `type` is an enum in Xano — the two
// values the expense form already uses.
const EMPTY = {
  name: '', type: 'นิติบุคคล', code: '', taxid: '', address: '',
  phone: '', cellphone: '', email: '',
  bank: '', banknum: '', bank_branchnum: '',
  cp_name: '', cp_phone: '', cp_line: '', cp_email: '',
}

/**
 * Edit one seller (the central `seller` master record) — bank + contact-person
 * detail lives here, not on the expense. Saving does NOT touch the frozen
 * seller snapshot on past expenses; onSaved lets the caller refresh the fields
 * it does mirror (name / taxid / phone / address).
 */
export default function SellerEditor({ open, sellerId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const set = (patch) => { setMsg(null); setForm((f) => ({ ...f, ...patch })) }

  useEffect(() => {
    if (!open || !sellerId) return
    let alive = true
    setLoading(true); setMsg(null); setForm(EMPTY)
    apiGet(`/api/sellers/${sellerId}`)
      .then((r) => {
        if (!alive) return
        const s = r?.seller || r || {}
        setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, s[k] ?? EMPTY[k]])) })
      })
      .catch((e) => { if (alive) setMsg({ ok: false, text: xanoMessage(e) }) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [open, sellerId])

  async function save() {
    if (!form.name.trim()) { setMsg({ ok: false, text: 'กรุณากรอกชื่อบริษัท' }); return }
    setSaving(true); setMsg(null)
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v]),
      )
      await patchJson(`/api/sellers/${sellerId}`, payload)
      setMsg({ ok: true, text: 'บันทึกแล้ว' })
      onSaved?.({ id: sellerId, ...payload })
    } catch (e) {
      setMsg({ ok: false, text: xanoMessage(e) })
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col animate-ss-slide-in">
        <div className="px-5 pt-4 pb-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[16px] font-semibold text-slate-900">
            <Store size={18} className="text-brand-600" /> ข้อมูลบริษัท / ผู้ขาย
            <span className="text-[12px] font-normal text-slate-400">#{sellerId}</span>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5 space-y-6">
            <Group icon={Building2} title="ข้อมูลบริษัท">
              <Field label="ชื่อบริษัท หรือผู้ขาย">
                <input value={form.name} onChange={(e) => set({ name: e.target.value })} className={FIELD} />
              </Field>
              <div>
                <span className={LABEL}>ประเภท</span>
                <div className="flex gap-2">
                  {[['นิติบุคคล', Building2], ['บุคคลธรรมดา', User]].map(([t, Icon]) => (
                    <button key={t} type="button" onClick={() => set({ type: t })}
                      className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-[13px] font-medium transition ${form.type === t ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                      <Icon size={14} /> {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="เลขผู้เสียภาษี">
                  <input value={form.taxid} onChange={(e) => set({ taxid: e.target.value })} placeholder="0123456789101" className={FIELD} />
                </Field>
                <Field label="รหัสบริษัท">
                  <input value={form.code} onChange={(e) => set({ code: e.target.value })} className={FIELD} />
                </Field>
              </div>
              <Field label="ที่อยู่">
                <textarea value={form.address} onChange={(e) => set({ address: e.target.value })} rows={2} className={`${FIELD} resize-none`} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="เบอร์โทร">
                  <input value={form.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="02-000-0000" className={FIELD} />
                </Field>
                <Field label="มือถือ">
                  <input value={form.cellphone} onChange={(e) => set({ cellphone: e.target.value })} placeholder="08x-xxx-xxxx" className={FIELD} />
                </Field>
              </div>
              <Field label="อีเมล">
                <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="account@company.com" className={FIELD} />
              </Field>
            </Group>

            <Group icon={Landmark} title="บัญชีธนาคาร">
              <Field label="ธนาคาร">
                <Combobox
                  value={form.bank}
                  onChange={(v) => set({ bank: v })}
                  options={THAI_BANKS}
                  placeholder="เลือกธนาคาร"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="เลขที่บัญชี">
                  <input value={form.banknum} onChange={(e) => set({ banknum: e.target.value })} placeholder="xxx-x-xxxxx-x" className={FIELD} />
                </Field>
                <Field label="สาขา">
                  <input value={form.bank_branchnum} onChange={(e) => set({ bank_branchnum: e.target.value })} className={FIELD} />
                </Field>
              </div>
            </Group>

            <Group icon={UserRound} title="ผู้ติดต่อ">
              <div className="grid grid-cols-2 gap-3">
                <Field label="ชื่อผู้ติดต่อ">
                  <input value={form.cp_name} onChange={(e) => set({ cp_name: e.target.value })} className={FIELD} />
                </Field>
                <Field label="เบอร์โทร">
                  <input value={form.cp_phone} onChange={(e) => set({ cp_phone: e.target.value })} className={FIELD} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="LINE">
                  <input value={form.cp_line} onChange={(e) => set({ cp_line: e.target.value })} className={FIELD} />
                </Field>
                <Field label="อีเมล">
                  <input type="email" value={form.cp_email} onChange={(e) => set({ cp_email: e.target.value })} className={FIELD} />
                </Field>
              </div>
            </Group>
          </div>
        )}

        <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-3">
          {msg && (
            <span className={`flex items-center gap-1 text-[12px] ${msg.ok ? 'text-emerald-600' : 'text-rose-600'}`}>
              {msg.ok ? <Check size={13} /> : <AlertCircle size={13} />} {msg.text}
            </span>
          )}
          <button
            onClick={save}
            disabled={saving || loading}
            className="ml-auto inline-flex items-center gap-1.5 rounded-xl bg-brand-600 px-4 py-2 text-[13.5px] font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} บันทึก
          </button>
        </div>
      </div>
    </div>
  )
}
